/**
 * Manages the user's nutrition profile.
 * This data feeds the Cerveau's meal plan generation (Module 1B).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface NutritionProfileData {
  wake_time: string | null;
  sleep_time: string | null;
  meal_times: string[];
  diet_type: string | null;
  allergies: string[];
  intolerances: string[];
  liked_foods: string[];
  disliked_foods: string[];
  work_context: string | null;
  kitchen_access_midday: boolean;
  budget_monthly_eur: number;
  current_supplements: string[];
  nutrition_goal: string | null;
  completed_at: string | null;
}

export const DEFAULT_NUTRITION_PROFILE: NutritionProfileData = {
  wake_time: "07:00",
  sleep_time: "23:00",
  meal_times: ["07:30", "12:00", "19:30"],
  diet_type: "omnivore",
  allergies: [],
  intolerances: [],
  liked_foods: [],
  disliked_foods: [],
  work_context: "office",
  kitchen_access_midday: true,
  budget_monthly_eur: 0,
  current_supplements: [],
  nutrition_goal: null,
  completed_at: null,
};

export function useNutritionProfile() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ["nutrition-profile", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("nutrition_profile")
        .select("*")
        .eq("user_id", user!.id)
        .maybeSingle();
      return (data as NutritionProfileData | null) ?? null;
    },
  });

  const isCompleted = !!profile?.completed_at;

  const saveProfile = async (updates: Partial<NutritionProfileData>) => {
    if (!user) return;
    const payload = {
      user_id: user.id,
      ...updates,
      completed_at: new Date().toISOString(),
    };

    const { error } = await supabase
      .from("nutrition_profile" as any)
      .upsert(payload as any, { onConflict: "user_id" });

    if (error) {
      toast.error("Erreur lors de la sauvegarde.");
      throw error;
    }

    toast.success("Profil nutrition enregistré ✓");
    qc.invalidateQueries({ queryKey: ["nutrition-profile"] });
  };

  return {
    profile: profile ?? DEFAULT_NUTRITION_PROFILE,
    isCompleted,
    loading: isLoading,
    saveProfile,
  };
}
